import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Brain } from "lucide-react";
import { MatchScoreDisplay } from "@/components/ui/MatchScoreDisplay";
import { RecommendationBadge } from "@/components/ui/RecommendationBadge";
import { Badge } from "@/components/ui/badge";
import { MarkdownRenderer } from "@/components/admin/MarkdownRenderer";
import { AdminReferral, type Job, type User as UserType } from "@/lib/schema";
import { AdminReferralWithUI } from "@/api/referrals";

interface ReferralViewerData {
  job: Job | null;
  referrer: UserType | null;
  candidateName: string;
  candidateSkills: string[];
  jobSkills: string[];
  matchingSkills: string[];
  missingSkills: string[];
}

interface AIInsightsTabProps {
  referral: AdminReferralWithUI;
  viewerData: ReferralViewerData | null;
  isLoadingData: boolean;
  getMatchScore: (referral: AdminReferral) => number;
}

export function AIInsightsTab({ 
  referral, 
  viewerData, 
  isLoadingData, 
  getMatchScore
}: AIInsightsTabProps) {
  return (
    <div className="space-y-6">
      {/* Recommendation Banner */}
      {referral.recommendation && (
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Brain className="h-6 w-6 text-purple-600" />
                <div>
                  <h3 className="text-lg font-semibold space-grotesk text-gray-900">AI Recommendation</h3>
                  <RecommendationBadge
                    recommendation={referral.recommendation}
                    size="md"
                  />
                </div>
              </div>
              <MatchScoreDisplay
                matchScore={getMatchScore(referral)}
                recommendation={referral.recommendation}
                size="lg"
                showLabel={true}
              />
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardContent>
            <MarkdownRenderer 
              content={referral.overallAnalysis || 
                'AI analysis is being processed. This candidate\'s profile will be evaluated against job requirements to provide detailed insights and recommendations.'}
              className="text-sm text-gray-700 leading-relaxed"
            />
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="space-grotesk text-lg">Skills Gap Analysis</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-green-700 space-grotesk mb-2">Matching Skills</h4>
              <div className="flex flex-wrap gap-1">
                {isLoadingData ? (
                  <span className="text-xs text-gray-500 space-grotesk">Loading...</span>
                ) : viewerData?.matchingSkills.length ? (
                  viewerData.matchingSkills.map((skill, index) => (
                    <Badge key={index} className="bg-green-100 text-green-700 border-0 space-grotesk text-xs">
                      {skill}
                    </Badge>
                  ))
                ) : (
                  <span className="text-xs text-gray-500 space-grotesk">No matching skills identified</span>
                )}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-orange-700 space-grotesk mb-2">Missing Skills</h4>
              <div className="flex flex-wrap gap-1">
                {isLoadingData ? (
                  <span className="text-xs text-gray-500 space-grotesk">Loading...</span>
                ) : viewerData?.missingSkills.length ? (
                  viewerData.missingSkills.map((skill, index) => (
                    <Badge key={index} className="bg-orange-100 text-orange-700 border-0 space-grotesk text-xs">
                      {skill}
                    </Badge>
                  ))
                ) : (
                  <span className="text-xs text-green-600 space-grotesk">All required skills present</span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

    </div>
  );
}
